interface StoriesSectionProps {
  onOpenModal: () => void;
}

export default function StoriesSection({ onOpenModal }: StoriesSectionProps) {
  return (
    <section id="stories" className="w-full bg-background-200 py-10 md:py-20 relative overflow-hidden scroll-mt-20">
      {/* Magic sparkle decorations */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <div className="w-12 h-12 flex items-center justify-center absolute top-6 left-[8%] opacity-[0.06] rotate-[15deg] animate-float" style={{ animationDelay: '0s' }}>
          <i className="fa-solid fa-sparkles text-4xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute top-12 right-[10%] opacity-[0.05] rotate-[-25deg] animate-drift" style={{ animationDelay: '0.9s' }}>
          <i className="fa-solid fa-star text-5xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute top-[25%] right-[5%] opacity-[0.07] rotate-[30deg] animate-float" style={{ animationDelay: '1.8s' }}>
          <i className="fa-solid fa-sparkles text-3xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute top-[55%] left-[4%] opacity-[0.05] rotate-[-40deg] animate-drift" style={{ animationDelay: '2.7s' }}>
          <i className="fa-solid fa-sparkles text-3xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute top-[50%] right-[3%] opacity-[0.04] rotate-[20deg] animate-float" style={{ animationDelay: '3.6s' }}>
          <i className="fa-solid fa-star text-5xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute bottom-20 left-[15%] opacity-[0.06] rotate-[-15deg] animate-drift" style={{ animationDelay: '4.5s' }}>
          <i className="fa-solid fa-sparkles text-4xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute bottom-12 right-[12%] opacity-[0.05] rotate-[45deg] animate-float" style={{ animationDelay: '5.4s' }}>
          <i className="fa-solid fa-sparkles text-3xl text-foreground-950"></i>
        </div>
        <div className="w-16 h-16 flex items-center justify-center absolute top-[35%] left-[45%] opacity-[0.04] rotate-[10deg] animate-drift" style={{ animationDelay: '2.2s' }}>
          <i className="fa-solid fa-sparkles text-6xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute bottom-[30%] right-[25%] opacity-[0.06] rotate-[-35deg] animate-float" style={{ animationDelay: '1.2s' }}>
          <i className="fa-solid fa-star text-3xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute top-[70%] left-[30%] opacity-[0.05] rotate-[55deg] animate-drift" style={{ animationDelay: '3.0s' }}>
          <i className="fa-solid fa-sparkles text-4xl text-foreground-950"></i>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
        <h2 className="text-center text-3xl md:text-4xl font-black text-primary-500 mb-3">
          Stories That Spark Imagination & Build Character
        </h2>
        <p className="text-center text-foreground-700 text-base md:text-lg mb-4 md:mb-6 max-w-3xl mx-auto">
          Each Gracie and Lily adventure is crafted to inspire young minds with timeless values while taking them on magical journeys they'll never forget.
        </p>

        <h3 className="text-center font-black mb-6 md:mb-12 text-xl md:text-2xl text-foreground-950">Imagination Soars!</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
          <div className="rounded-2xl overflow-hidden flex flex-col bg-white/60">
            <div className="w-full bg-white/80 flex items-center justify-center p-3">
              <img
                alt="Gracie and Lily children's adventure story illustration featuring gentle forest animal friends"
                title="Gracie & Lily kindness and friendship story illustration"
                className="w-full h-auto max-h-60 object-contain"
                src="https://storage.readdy-site.link/project_files/61f6dd57-4435-4fb1-9f7a-943f2831e007/3460033f-03a1-4cfd-8ca7-02620138428b_FOREST-ANIMALS.png?v=c30ed910c50daa13e034a9836cecc62a"
              />
            </div>
            <div className="p-5 md:p-6 flex flex-col gap-2">
              <h4 className="text-lg font-extrabold text-primary-500">Teaches Kindness & Empathy</h4>
              <p className="text-foreground-700 text-sm leading-relaxed">
                Learn valuable life lessons about friendship, caring for others, and being a good friend.
              </p>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden flex flex-col bg-white/60">
            <div className="w-full bg-white/80 flex items-center justify-center p-3">
              <img
                alt="Gracie and Lily children's adventure story illustration encouraging creativity and imagination"
                title="Gracie & Lily imagination and creativity story illustration"
                className="w-full h-auto max-h-60 object-contain"
                src="https://storage.readdy-site.link/project_files/61f6dd57-4435-4fb1-9f7a-943f2831e007/1aae75ec-129d-4973-bc06-b9be55a575ea_CREATIVITY.png?v=cb8ad9b571c61136a1820ce2a468ea94"
              />
            </div>
            <div className="p-5 md:p-6 flex flex-col gap-2">
              <h4 className="text-lg font-extrabold text-primary-500">Encourages Creativity</h4>
              <p className="text-foreground-700 text-sm leading-relaxed">
                Vibrant illustrations and magical storylines inspire imagination and creative thinking.
              </p>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden flex flex-col bg-white/60">
            <div className="w-full bg-white/80 flex items-center justify-center p-3">
              <img
                alt="Gracie and Lily children's adventure story illustration about courage and bravery"
                title="Gracie & Lily courage and confidence story illustration"
                className="w-full h-auto max-h-60 object-contain"
                src="https://storage.readdy-site.link/project_files/61f6dd57-4435-4fb1-9f7a-943f2831e007/6e197393-7037-436f-a418-7e8ef1044991_COURAGE.png?v=3739788a9bf30a57434d1f52cb748d63"
              />
            </div>
            <div className="p-5 md:p-6 flex flex-col gap-2">
              <h4 className="text-lg font-extrabold text-primary-500">Builds Courage & Confidence</h4>
              <p className="text-foreground-700 text-sm leading-relaxed">
                Follow Gracie and Lily as they face challenges with bravery and determination.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
          <a
            href="https://www.youtube.com/@GracieandLilyAdventures"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary-500 text-white px-6 md:px-7 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors text-center"
          >
            Watch Storytime Videos
          </a>
          <button
            onClick={() => onOpenModal()}
            className="border-2 border-primary-500 text-primary-500 px-6 md:px-7 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-500 hover:text-white transition-colors"
          >
            Get Free Coloring Book
          </button>
        </div>
      </div>
    </section>
  );
}